import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";

export function useContactsPaginated(params?: {
  page?: number;
  limit?: number;
  search?: string;
  nameSearch?: string;
  companySearch?: string;
  emailSearch?: string;
  phoneSearch?: string;
  addressSearch?: string;
  citySearch?: string;
  stateSearch?: string;
  zipCodeSearch?: string;
  countrySearch?: string;
  websiteSearch?: string;
  notesSearch?: string;
  status?: string | string[];
  contactType?: string | string[];
  industry?: string | string[];
  tags?: string | string[];
  source?: string | string[];
  lifecycleStage?: string;
  isActive?: boolean;
  company_name?: string;
  exclude_id?: string;
}) {
  return useQuery({
    queryKey: ["contacts-paginated", params],
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: async () => {
      console.log("🔍 useContactsPaginated: queryFn start", { params });
      const supabase = getSupabase();

      let countQuery = supabase.from("contacts").select("*", { count: "exact", head: true });
      let dataQuery = supabase.from("contacts").select("*");

      if (params?.status) {
        if (Array.isArray(params.status) && params.status.length > 0) {
          countQuery = countQuery.in("status", params.status);
          dataQuery = dataQuery.in("status", params.status);
        } else if (typeof params.status === "string") {
          countQuery = countQuery.eq("status", params.status);
          dataQuery = dataQuery.eq("status", params.status);
        }
      }
      if (params?.industry) {
        if (Array.isArray(params.industry) && params.industry.length > 0) {
          countQuery = countQuery.in("industry", params.industry);
          dataQuery = dataQuery.in("industry", params.industry);
        } else if (typeof params.industry === "string") {
          countQuery = countQuery.eq("industry", params.industry);
          dataQuery = dataQuery.eq("industry", params.industry);
        }
      }
      if (params?.source) {
        if (Array.isArray(params.source) && params.source.length > 0) {
          countQuery = countQuery.in("source", params.source);
          dataQuery = dataQuery.in("source", params.source);
        } else if (typeof params.source === "string") {
          countQuery = countQuery.eq("source", params.source);
          dataQuery = dataQuery.eq("source", params.source);
        }
      }
      if (params?.tags) {
        if (Array.isArray(params.tags) && params.tags.length > 0) {
          countQuery = countQuery.overlaps("tags", params.tags);
          dataQuery = dataQuery.overlaps("tags", params.tags);
        } else if (typeof params.tags === "string") {
          countQuery = countQuery.contains("tags", [params.tags]);
          dataQuery = dataQuery.contains("tags", [params.tags]);
        }
      }
      if (params?.isActive !== undefined) {
        countQuery = countQuery.eq("is_active", params.isActive);
        dataQuery = dataQuery.eq("is_active", params.isActive);
      }
      if (params?.search) {
        const searchFilter = `name.ilike.%${params.search}%,company_name.ilike.%${params.search}%,email.ilike.%${params.search}%`;
        countQuery = countQuery.or(searchFilter);
        dataQuery = dataQuery.or(searchFilter);
      }

      const specificSearchConditions: string[] = [];
      if (params?.nameSearch) specificSearchConditions.push(`name.ilike.%${params.nameSearch}%`);
      if (params?.companySearch) specificSearchConditions.push(`company_name.ilike.%${params.companySearch}%`);
      if (params?.emailSearch) {
        specificSearchConditions.push(
          `email.ilike.%${params.emailSearch}%,email_aliases.cs.{"*${params.emailSearch}*"}`
        );
      }
      if (params?.phoneSearch) specificSearchConditions.push(`phone.ilike.%${params.phoneSearch}%`);
      if (params?.addressSearch) specificSearchConditions.push(`address.ilike.%${params.addressSearch}%`);
      if (params?.citySearch) specificSearchConditions.push(`city.ilike.%${params.citySearch}%`);
      if (params?.stateSearch) specificSearchConditions.push(`state.ilike.%${params.stateSearch}%`);
      if (params?.zipCodeSearch) specificSearchConditions.push(`zip_code.ilike.%${params.zipCodeSearch}%`);
      if (params?.countrySearch) specificSearchConditions.push(`country.ilike.%${params.countrySearch}%`);
      if (params?.websiteSearch) specificSearchConditions.push(`website.ilike.%${params.websiteSearch}%`);
      if (params?.notesSearch) specificSearchConditions.push(`notes.ilike.%${params.notesSearch}%`);

      if (specificSearchConditions.length > 0) {
        const specificSearchFilter = specificSearchConditions.join(",");
        countQuery = countQuery.or(specificSearchFilter);
        dataQuery = dataQuery.or(specificSearchFilter);
      }
      if (params?.company_name) {
        countQuery = countQuery.eq("company_name", params.company_name);
        dataQuery = dataQuery.eq("company_name", params.company_name);
      }
      if (params?.exclude_id) {
        countQuery = countQuery.neq("id", params.exclude_id);
        dataQuery = dataQuery.neq("id", params.exclude_id);
      }

      const { count, error: countError } = await countQuery;
      if (countError) {
        console.error("Error fetching contacts count:", {
          message: countError.message,
          details: countError.details,
          hint: countError.hint,
          code: countError.code,
        });
        throw new Error(countError.message || "Failed to fetch contacts count");
      }

      if (params?.limit) {
        const offset = ((params.page || 1) - 1) * params.limit;
        dataQuery = dataQuery.range(offset, offset + params.limit - 1);
      }

      const { data, error: dataError } = await dataQuery.order("created_at", { ascending: false });

      if (dataError) {
        console.error("Error fetching contacts data:", dataError);
        throw new Error(dataError.message);
      }

      const result = {
        data: data || [],
        totalCount: count || 0,
        page: params?.page || 1,
        limit: params?.limit || 10,
        totalPages: Math.ceil((count || 0) / (params?.limit || 10)),
      };

      console.log("✅ useContactsPaginated: queryFn success", {
        dataCount: result.data.length,
        totalCount: result.totalCount,
        page: result.page,
        totalPages: result.totalPages,
      });

      return result;
    },
  });
}

export function useCompanyNames(params?: { search?: string; limit?: number }) {
  return useQuery({
    queryKey: ["company-names", params],
    queryFn: async () => {
      const supabase = getSupabase();
      let query = supabase
        .from("contacts")
        .select("company_name")
        .not("company_name", "is", null)
        .not("company_name", "eq", "");

      if (params?.search) {
        query = query.ilike("company_name", `%${params.search}%`);
      }
      if (params?.limit) {
        query = query.limit(params.limit);
      }

      const { data, error } = await query.order("company_name");

      if (error) {
        console.error("Error fetching company names:", error);
        throw new Error(error.message);
      }

      const uniqueCompanies = Array.from(
        new Set(data?.map((item) => item.company_name).filter(Boolean))
      ).map((company_name) => ({
        id: company_name,
        name: company_name,
        company_name,
      }));

      return uniqueCompanies;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCompanySearch(searchTerm: string) {
  return useCompanyNames({ search: searchTerm, limit: 20 });
}
